(function () {
  const selectors = {
    section: '[data-announcement-static]',
  };

  const STORAGE_PREFIX = 'announcementStaticDismiss';
  const AUTO_DISMISS_PREFIX = 'announcementStaticAutoDismiss';
  const SESSION_STORAGE_KEY = 'announcementBarPageViews';
  const FIRST_SESSION_KEY = 'announcementBarFirstSession';
  const FIRST_SESSION_SEEN_KEY = 'announcementBarFirstSessionSeen'; // Track if shown in current session
  const FIRST_PAGE_VIEW_DONE_KEY = 'announcementBarFirstPageViewDone'; // Bar already shown on a previous page view this session

  function ready(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn);
    } else {
      fn();
    }
  }

  class AnnouncementBar {
    constructor(root) {
      this.root = root;
      this.sectionId = root.dataset.sectionId;
      this.dismissDuration = parseInt(root.dataset.dismissHours, 10) || 48;
      this.autoDismissEnabled = root.dataset.autoDismissEnabled === 'true';
      this.autoDismissPageViews = parseInt(root.dataset.autoDismissPageViews, 10) || 3;
      this.autoDismissHours = parseInt(root.dataset.autoDismissHours, 10) || 24;
      this.showOnlyFirstSession = root.dataset.showOnlyFirstSession === 'true';
      this.firstSessionHideHours = parseInt(root.dataset.firstSessionHideHours, 10) || 24;
      this.dismissButton = root.querySelector('.announcement-static__dismiss');
      this.countdownEl = root.querySelector('[data-countdown]');
      this.expiredMessageEl = root.querySelector('[data-expired-message]');
      this.countdownBoxes = root.querySelectorAll('[data-countdown-part]');
      this.showCountdown = root.dataset.showCountdown === 'true';
      this.dailyReset = root.dataset.dailyReset === 'true';
      this.endDate = root.dataset.endDate || '';
      this.endTime = root.dataset.endTime || '00:00';
      this.expiredBehavior = root.dataset.expiredBehavior || 'hide_bar';
      this.expiredText = root.dataset.expiredMessage || '';
      this.countdownLabels = [
        root.dataset.countdownLabelDays || 'DAYS',
        root.dataset.countdownLabelHours || 'HRS',
        root.dataset.countdownLabelMinutes || 'MINS',
        root.dataset.countdownLabelSeconds || 'SECS',
      ];
      this.hideCountdownXs = root.dataset.hideCountdownXs === 'true';
      this.hideCountdownMobile = root.dataset.hideCountdownMobile === 'true';
      this.popupEnabled = root.dataset.popupEnabled === 'true';
      this.popup = document.getElementById(root.dataset.popupId);
      this.modal = this.popup ? this.popup.querySelector('.announcement-static__modal') : null;
      this.popupTrigger = root.querySelector('[data-popup-trigger]');
      this.ctaLink = root.querySelector('.announcement-static__cta[href]'); // CTA link when popup is disabled
      this.wholeBarClickable = root.dataset.wholeBarClickable === 'true';
      this.barDestinationUrl = root.dataset.barDestinationUrl || '';
      this.wrapperEl = root.querySelector('.announcement-static__wrapper');
      this.overlayClose = root.dataset.popupOverlayClose === 'true';
      this.copyDuration = parseInt(root.dataset.copyDuration, 10) || 2000;
      this.popupAnimation = root.dataset.popupAnimation || 'scale';
      this.barAnimation = root.dataset.barAnimation || 'slide-up';
      this.animationDuration = parseInt(root.dataset.animationDuration, 10) || 300;
      this.body = document.body;
      this.countdownRendered = false;
      this.interval = null;
      this.activeCopyTimeouts = new Map();
      this.focusedBeforePopup = null;
      this.focusHandler = this.handleFocusTrap.bind(this);
      this.keyHandler = this.handleKeydown.bind(this);
      this.touchStartY = null;
      this.touchDeltaY = 0;
      this.touching = false;
      this.dismissed = false;
      this.copyStatusEl = this.popup ? this.popup.querySelector('[data-copy-status]') : null;
      
      // Check for blocking style from inline script
      const blockingStyle = document.getElementById('announcement-bar-prevent-flash');
      if (true && typeof console !== 'undefined' && console.log) {
        console.log('[AnnouncementBar Debug][JRSBASE-V8.3.0-09102026-BYOB] AnnouncementBar instance', {
          sectionId: this.sectionId,
          hasBlockingStyle: !!blockingStyle,
          dismissDuration: this.dismissDuration,
          autoDismissEnabled: this.autoDismissEnabled,
          showOnlyFirstSession: this.showOnlyFirstSession
        });
      }
      this.handleFirstSession();
      this.handleDismissedState();
      this.handleAutoDismiss();
      this.bindEvents();
      if (this.showCountdown) {
        this.ensureCountdownBoxes();
        this.startCountdown();
      }
      
      // Final visibility check
      setTimeout(() => {
        // Visibility check complete
      }, 100);
    }

    /** Remove the inline style that hides the bar (injected by theme.liquid). Call when bar should be visible. */
    removePreventFlashStyle() {
      const el = document.getElementById('announcement-bar-prevent-flash');
      if (el) el.remove();
    }

    ensureCountdownBoxes() {
      if (!this.countdownEl) return;
      if (this.countdownBoxes.length > 0) return;
      const fragment = document.createDocumentFragment();
      this.countdownLabels.forEach((label) => {
        const box = document.createElement('div');
        box.className = 'announcement-static__countdown-box';
        const value = document.createElement('span');
        value.className = 'announcement-static__countdown-number';
        value.dataset.countdownPart = '';
        value.textContent = '00';
        const labelEl = document.createElement('span');
        labelEl.className = 'announcement-static__countdown-label';
        labelEl.textContent = label;
        box.appendChild(value);
        box.appendChild(labelEl);
        fragment.appendChild(box);
      });
      this.countdownEl.appendChild(fragment);
      this.countdownBoxes = this.countdownEl.querySelectorAll('[data-countdown-part]');
    }

    handleFirstSession() {
      if (!this.showOnlyFirstSession) {
        if (true && typeof console !== 'undefined' && console.log) {
          console.log('[AnnouncementBar Debug][JRSBASE-V8.3.0-09102026-BYOB] handleFirstSession skipped (showOnlyFirstSession=false)');
        }
        return;
      }
      
      const localStorageKey = `${FIRST_SESSION_KEY}-${this.sectionId}`;
      const sessionStorageKey = `${FIRST_SESSION_SEEN_KEY}-${this.sectionId}`;
      const firstPageViewDoneKey = `${FIRST_PAGE_VIEW_DONE_KEY}-${this.sectionId}`;
      
      try {
        const now = Date.now();
        const durationMs = this.firstSessionHideHours * 60 * 60 * 1000; // Convert hours to milliseconds
        
        // Check if user has already seen it in this browser session
        const seenInSession = window.sessionStorage.getItem(sessionStorageKey);
        const storedTimestampVal = window.localStorage.getItem(localStorageKey);
        if (true && typeof console !== 'undefined' && console.log) {
          console.log('[AnnouncementBar Debug][JRSBASE-V8.3.0-09102026-BYOB] handleFirstSession', {
            showOnlyFirstSession: true,
            seenInSession: seenInSession === 'true',
            storedTimestamp: storedTimestampVal || null,
            firstSessionHideHours: this.firstSessionHideHours
          });
        }
        
        if (seenInSession === 'true') {
          // Same session (tab still open): show bar on every page until they close the tab
          this.removePreventFlashStyle();
          if (this.root) {
            this.root.classList.remove('announcement-static--hidden');
            this.root.classList.remove('announcement-static--dismiss-fade');
            this.root.classList.remove('announcement-static--dismiss-slide-up');
            this.root.classList.remove('announcement-static--dismiss-none');
          }
          window.sessionStorage.setItem(firstPageViewDoneKey, 'true');
          return;
        }
        
        // Not seen in this session yet - check if this is their first session ever
        if (storedTimestampVal) {
          // User has seen it in a previous session - check if hide duration has expired
          const timestamp = parseInt(storedTimestampVal, 10);
          const timeDiff = now - timestamp;
          const timeDiffHours = timeDiff / (60 * 60 * 1000);
          const timeDiffDays = timeDiffHours / 24;
          const isExpired = timeDiff >= durationMs;
          
          // Check for suspicious timestamps (future timestamps or very recent when expecting old)
          const isFutureTimestamp = timestamp > now;
          const isVeryRecent = timeDiffHours < 1 && timeDiffDays < 0.1; // Less than 1 hour old
          
          // If timestamp is in the future, treat it as invalid and show the bar
          if (isFutureTimestamp) {
            window.localStorage.removeItem(localStorageKey);
            window.sessionStorage.setItem(sessionStorageKey, 'true');
            this.removePreventFlashStyle();
            if (this.root) {
              this.root.classList.remove('announcement-static--hidden');
              this.root.classList.remove('announcement-static--dismiss-fade');
              this.root.classList.remove('announcement-static--dismiss-slide-up');
              this.root.classList.remove('announcement-static--dismiss-none');
              this.root.style.display = '';
              this.root.style.visibility = '';
              this.root.style.opacity = '';
            }
            window.sessionStorage.setItem(firstPageViewDoneKey, 'true');
            return;
          }
          
          if (now - timestamp < durationMs) {
            // Still within the hide duration, hide the bar
            if (typeof console !== 'undefined' && console.log) {
              console.log('[AnnouncementBar Debug][JRSBASE-V8.3.0-09102026-BYOB] handleFirstSession hiding bar (within firstSessionHideHours)');
            }
            this.hideBar(false);
            this.dismissed = true;
            return;
          } else {
            // Hide duration has expired - this is a "new first session"
            // Mark timestamp and show on first page view only
            window.localStorage.setItem(localStorageKey, now.toString());
            window.sessionStorage.setItem(sessionStorageKey, 'true');
            this.removePreventFlashStyle();
            if (this.root) {
              this.root.classList.remove('announcement-static--hidden');
              this.root.classList.remove('announcement-static--dismiss-fade');
              this.root.classList.remove('announcement-static--dismiss-slide-up');
              this.root.classList.remove('announcement-static--dismiss-none');
              this.root.style.display = '';
              this.root.style.visibility = '';
              this.root.style.opacity = '';
            }
            window.sessionStorage.setItem(firstPageViewDoneKey, 'true');
          }
        } else {
          // First time ever - mark timestamp and show on first page view only
          window.localStorage.setItem(localStorageKey, now.toString());
          window.sessionStorage.setItem(sessionStorageKey, 'true');
          this.removePreventFlashStyle();
          if (this.root) {
            this.root.classList.remove('announcement-static--hidden');
            this.root.classList.remove('announcement-static--dismiss-fade');
            this.root.classList.remove('announcement-static--dismiss-slide-up');
            this.root.classList.remove('announcement-static--dismiss-none');
          }
          window.sessionStorage.setItem(firstPageViewDoneKey, 'true');
        }
      } catch (e) {
        // Storage might be disabled, continue normally
      }
    }

    bindEvents() {
      if (this.dismissButton) {
        this.dismissButton.addEventListener('click', () => this.dismissBar());
      }
      if (this.popupEnabled && this.popup && this.popupTrigger) {
        this.popupTrigger.addEventListener('click', (event) => {
          event.preventDefault();
          this.openPopup();
        });
      }

      if (this.wholeBarClickable && this.wrapperEl) {
        this.wrapperEl.addEventListener('click', (event) => this.handleWrapperClick(event));
      }
      if (this.popup) {
        const closeBtn = this.popup.querySelector('[data-popup-close]');
        if (closeBtn) {
          closeBtn.addEventListener('click', () => this.closePopup());
        }

        const overlay = this.popup.querySelector('.announcement-static__overlay');
        if (overlay && this.overlayClose) {
          overlay.addEventListener('click', () => this.closePopup());
        }

        this.popup.querySelectorAll('[data-copy-code]').forEach((button) => {
          button.addEventListener('click', () => this.copyCode(button));
        });

        if (this.modal) {
          this.modal.addEventListener('touchstart', (event) => this.handleTouchStart(event), { passive: true });
          this.modal.addEventListener('touchmove', (event) => this.handleTouchMove(event), { passive: false });
          this.modal.addEventListener('touchend', () => this.handleTouchEnd());
        }
      }
    }

    handleDismissedState() {
      const key = `${STORAGE_PREFIX}-${this.sectionId}`;
      const autoDismissKey = `${AUTO_DISMISS_PREFIX}-${this.sectionId}`;
      let wasDismissed = false;
      const debug = (true && typeof console !== 'undefined' && console.log) ? { key, autoDismissKey, manual: null, auto: null, action: null } : null;
      
      try {
        // Check manual dismiss first
        const stored = window.localStorage.getItem(key);
        if (debug) debug.manual = stored ? { ts: stored, durationHrs: this.dismissDuration } : null;
        
        if (stored) {
          const timestamp = parseInt(stored, 10);
          const now = Date.now();
          const durationMs = this.dismissDuration * 60 * 60 * 1000;
          const timeDiff = now - timestamp;
          const timeDiffHours = timeDiff / (60 * 60 * 1000);
          const isExpired = timeDiff >= durationMs;
          if (debug) debug.action = isExpired ? 'manual_expired_removed' : 'hide_manual_dismiss';
          if (debug) console.log('[AnnouncementBar Debug][JRSBASE-V8.3.0-09102026-BYOB] handleDismissedState', debug);
          
          if (now - timestamp < durationMs) {
            // Still within dismiss duration - hide the bar
            this.hideBar(false);
            this.dismissed = true;
            return;
          } else {
            // Expiration time has passed - remove the localStorage item
            window.localStorage.removeItem(key);
            wasDismissed = true; // Mark that it was previously dismissed but now expired
          }
        }

        // Check auto-dismiss
        const autoStored = window.localStorage.getItem(autoDismissKey);
        if (debug) debug.auto = autoStored ? { ts: autoStored, durationHrs: this.autoDismissHours } : null;
        
        if (autoStored && !this.autoDismissEnabled) {
          // Auto-dismiss is off: clear an old auto-dismiss stamp so the bar shows again
          window.localStorage.removeItem(autoDismissKey);
        } else if (autoStored) {
          const timestamp = parseInt(autoStored, 10);
          const now = Date.now();
          const durationMs = this.autoDismissHours * 60 * 60 * 1000;
          const timeDiff = now - timestamp;
          const timeDiffHours = timeDiff / (60 * 60 * 1000);
          const isExpired = timeDiff >= durationMs;
          if (debug && !debug.action) debug.action = isExpired ? 'auto_expired_removed' : 'hide_auto_dismiss';
          if (debug && !debug.action) console.log('[AnnouncementBar Debug][JRSBASE-V8.3.0-09102026-BYOB] handleDismissedState', debug);
          
          if (now - timestamp < durationMs) {
            // Still within auto-dismiss duration - hide the bar
            this.hideBar(false);
            this.dismissed = true;
            return;
          } else {
            // Expiration time has passed - remove the localStorage item
            window.localStorage.removeItem(autoDismissKey);
            wasDismissed = true; // Mark that it was previously dismissed but now expired
          }
        }

        if (debug) { debug.action = wasDismissed ? 'show_after_expired' : 'show_not_dismissed'; console.log('[AnnouncementBar Debug][JRSBASE-V8.3.0-09102026-BYOB] handleDismissedState', debug); }

        // CRITICAL FIX: If expiration time has passed, explicitly show the bar again
        // Remove the hidden class and any animation classes that might be hiding it
        if (wasDismissed && this.root) {
          this.root.classList.remove('announcement-static--hidden');
          this.root.classList.remove('announcement-static--dismiss-fade');
          this.root.classList.remove('announcement-static--dismiss-slide-up');
          this.root.classList.remove('announcement-static--dismiss-none');
          // Reset any inline styles that might have been set
          this.root.style.display = '';
          this.root.style.visibility = '';
          this.root.style.opacity = '';
          this.root.style.height = '';
          this.root.style.overflow = '';
          this.root.style.maxHeight = '';
          this.root.style.padding = '';
          this.root.style.margin = '';
        }

        // If this section was NOT dismissed (or dismiss expired), remove the blocking style
        // so the bar can show. The prevent-flash stylesheet uses !important; clearing inline
        // styles is not enough — we must remove the style element.
        this.removePreventFlashStyle();
        if (this.root) {
          this.root.style.display = '';
          this.root.style.visibility = '';
          this.root.style.opacity = '';
          this.root.style.height = '';
          this.root.style.overflow = '';
          this.root.style.maxHeight = '';
          this.root.style.padding = '';
          this.root.style.margin = '';
        }
      } catch (error) {
        // Error handling
      }
      
      this.dismissed = false;
    }

    handleAutoDismiss() {
      if (!this.autoDismissEnabled) {
        if (true && typeof console !== 'undefined' && console.log) {
          console.log('[AnnouncementBar Debug][JRSBASE-V8.3.0-09102026-BYOB] handleAutoDismiss skipped (autoDismissEnabled=false)');
        }
        return;
      }
      
      try {
        // Check if already dismissed (don't count if already dismissed)
        const key = `${STORAGE_PREFIX}-${this.sectionId}`;
        const autoDismissKey = `${AUTO_DISMISS_PREFIX}-${this.sectionId}`;
        const stored = window.localStorage.getItem(key);
        const autoStored = window.localStorage.getItem(autoDismissKey);
        
        // If already dismissed (either manually or auto), don't increment counter
        if (stored) {
          const timestamp = parseInt(stored, 10);
          const now = Date.now();
          const durationMs = this.dismissDuration * 60 * 60 * 1000;
          if (now - timestamp < durationMs) {
            return;
          }
        }
        if (autoStored) {
          const timestamp = parseInt(autoStored, 10);
          const now = Date.now();
          const durationMs = this.autoDismissHours * 60 * 60 * 1000;
          if (now - timestamp < durationMs) {
            return;
          }
        }

        // Get current page view count from sessionStorage
        let pageViews = 0;
        try {
          const storedViews = window.sessionStorage.getItem(SESSION_STORAGE_KEY);
          if (storedViews) {
            pageViews = parseInt(storedViews, 10) || 0;
          }
        } catch (e) {
          // sessionStorage unavailable
        }

        // Increment page view count
        pageViews += 1;

        // Save updated count
        try {
          window.sessionStorage.setItem(SESSION_STORAGE_KEY, pageViews.toString());
        } catch (e) {
          // sessionStorage unavailable, can't track
          return;
        }
        if (true && typeof console !== 'undefined' && console.log) {
          console.log('[AnnouncementBar Debug][JRSBASE-V8.3.0-09102026-BYOB] handleAutoDismiss', { pageViews, autoDismissPageViews: this.autoDismissPageViews, willHide: pageViews >= this.autoDismissPageViews });
        }

        // Check if threshold is met
        if (pageViews >= this.autoDismissPageViews) {
          // Auto-dismiss the bar
          this.autoDismissBar();
        }
      } catch (error) {
        // Error handling
      }
    }

    autoDismissBar() {
      const autoDismissKey = `${AUTO_DISMISS_PREFIX}-${this.sectionId}`;
      this.hideBar(true);
      try {
        // Use separate key for auto-dismiss with auto-dismiss hours
        window.localStorage.setItem(autoDismissKey, Date.now().toString());
        this.dismissed = true;
      } catch (error) {
        // console.warn('Announcement bar: unable to persist auto-dismiss state', error);
      }
    }

    handleTouchStart(event) {
      if (window.innerWidth >= 768) return;
      this.touching = true;
      this.touchStartY = event.touches ? event.touches[0].clientY : event.clientY;
      this.touchDeltaY = 0;
    }

    handleTouchMove(event) {
      if (!this.touching || window.innerWidth >= 768) return;
      const currentY = event.touches ? event.touches[0].clientY : event.clientY;
      this.touchDeltaY = currentY - this.touchStartY;
      if (this.touchDeltaY > 0) {
        event.preventDefault();
        if (this.modal) {
          this.modal.style.transform = `translateY(${Math.min(this.touchDeltaY, 160)}px)`;
        }
      }
    }

    handleTouchEnd() {
      if (!this.touching || window.innerWidth >= 768) return;
      this.touching = false;
      if (this.touchDeltaY > 80) {
        this.closePopup();
      } else if (this.modal) {
        this.modal.style.transform = '';
      }
      this.touchStartY = null;
      this.touchDeltaY = 0;
    }

    dismissBar() {
      const key = `${STORAGE_PREFIX}-${this.sectionId}`;
      this.hideBar(true);
      try {
        window.localStorage.setItem(key, Date.now().toString());
      } catch (error) {
        // console.warn('Announcement bar: unable to persist dismiss state', error);
      }
    }

    /**
     * Whole-bar click: navigate or open popup. Dismiss button and CTA handle themselves.
     */
    handleWrapperClick(event) {
      if (!this.wholeBarClickable || !this.wrapperEl) return;
      const raw = event.target;
      const origin = raw instanceof Element ? raw : raw.parentElement;
      if (!origin || typeof origin.closest !== 'function') return;
      if (origin.closest('.announcement-static__dismiss')) return;
      if (origin.closest('.announcement-static__cta')) return;

      if (this.popupEnabled && this.popup) {
        event.preventDefault();
        this.openPopup();
        return;
      }

      const url = (this.barDestinationUrl || '').trim();
      if (!url || url === '#') return;
      window.location.assign(url);
    }

    hideBar(animated) {
      if (!this.root) {
        return;
      }
      
      if (animated && this.barAnimation !== 'none') {
        const animationClass = `announcement-static--dismiss-${this.barAnimation}`;
        this.root.style.setProperty('--announcement-static-animation-duration', `${this.animationDuration}ms`);
        this.root.classList.add(animationClass);
        setTimeout(() => {
          this.root.classList.add('announcement-static--hidden');
        }, this.animationDuration);
      } else {
        this.root.classList.add('announcement-static--hidden');
      }
      if (this.interval) {
        clearInterval(this.interval);
      }
    }

    getNextMidnightUK() {
      // Get current time in UK timezone
      const now = new Date();
      const formatter = new Intl.DateTimeFormat('en-GB', {
        timeZone: 'Europe/London',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      });
      const parts = formatter.formatToParts(now);
      const partMap = {};
      parts.forEach((p) => {
        if (p.type !== 'literal') {
          partMap[p.type] = p.value;
        }
      });
      
      const year = parseInt(partMap.year, 10);
      const month = parseInt(partMap.month, 10);
      const day = parseInt(partMap.day, 10);
      const hour = parseInt(partMap.hour, 10);
      const minute = parseInt(partMap.minute, 10);
      const second = parseInt(partMap.second, 10);
      
      // Get today's midnight UK time as UTC timestamp
      const todayMidnightUTC = this.getUKMidnightAsUTC(year, month, day);
      
      // If we've already passed midnight today, get tomorrow's midnight
      const nowUTC = Date.now();
      if (nowUTC >= todayMidnightUTC) {
        // Calculate tomorrow's date
        const tomorrow = new Date(year, month - 1, day);
        tomorrow.setDate(tomorrow.getDate() + 1);
        return this.getUKMidnightAsUTC(tomorrow.getFullYear(), tomorrow.getMonth() + 1, tomorrow.getDate());
      }
      
      return todayMidnightUTC;
    }

    getUKMidnightAsUTC(year, month, day) {
      // Create a UTC date for midnight on the given date (assumed to be UK date)
      const baseUtc = Date.UTC(year, month - 1, day, 0, 0, 0);
      const formatter = new Intl.DateTimeFormat('en-GB', {
        timeZone: 'Europe/London',
        hour12: false,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      });
      const parts = formatter.formatToParts(new Date(baseUtc));
      const partMap = {};
      parts.forEach((p) => {
        if (p.type !== 'literal') {
          partMap[p.type] = p.value;
        }
      });
      const formattedIso = `${partMap.year}-${partMap.month}-${partMap.day}T${partMap.hour || '00'}:${partMap.minute || '00'}:${partMap.second || '00'}Z`;
      const londonTimestamp = Date.parse(formattedIso);
      if (Number.isNaN(londonTimestamp)) return null;
      const diff = baseUtc - londonTimestamp;
      return baseUtc + diff;
    }

    getLondonTimestamp() {
      // If daily reset is enabled, always return next midnight UK time
      if (this.dailyReset) {
        return this.getNextMidnightUK();
      }
      
      if (!this.endDate) return null;
      const dateParts = this.endDate.split('-');
      if (dateParts.length !== 3) return null;
      const [year, month, day] = dateParts.map((part) => parseInt(part, 10));
      const timeParts = this.endTime.split(':');
      if (timeParts.length < 2) return null;
      const [hour, minute] = timeParts.map((part) => parseInt(part, 10));
      if (Number.isNaN(year) || Number.isNaN(month) || Number.isNaN(day)) return null;
      const baseUtc = Date.UTC(year, month - 1, day, hour, minute, 0);
      const formatter = new Intl.DateTimeFormat('en-GB', {
        timeZone: 'Europe/London',
        hour12: false,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      });
      const parts = formatter.formatToParts(new Date(baseUtc));
      const partMap = {};
      parts.forEach((p) => {
        if (p.type !== 'literal') {
          partMap[p.type] = p.value;
        }
      });
      const formattedIso = `${partMap.year}-${partMap.month}-${partMap.day}T${partMap.hour || '00'}:${partMap.minute || '00'}:${partMap.second || '00'}Z`;
      const londonTimestamp = Date.parse(formattedIso);
      if (Number.isNaN(londonTimestamp)) return null;
      const diff = baseUtc - londonTimestamp;
      return baseUtc + diff;
    }

    startCountdown() {
      const updateTarget = () => {
        // If daily reset is enabled, always get the current next midnight
        // Otherwise, get the fixed target timestamp
        return this.dailyReset ? this.getNextMidnightUK() : this.getLondonTimestamp();
      };
      
      let target = updateTarget();
      
      // When daily reset is enabled, we should always have a valid target (next midnight)
      // If not, keep retrying until we get one
      if (!target) {
        if (this.dailyReset) {
          // Retry getting next midnight - should always succeed
          target = this.getNextMidnightUK();
          if (!target) {
            // console.warn('Announcement bar: Failed to get next midnight UK time');
            return;
          }
        } else {
          // Only handle expired if daily reset is disabled
          this.handleExpired(false);
          return;
        }
      }
      
      // Check if already expired on initial load
      const now = Date.now();
      let diff = target - now;
      
      // If daily reset is enabled and we've passed midnight, get the next midnight
      if (this.dailyReset && diff <= 0) {
        target = updateTarget();
        if (target) {
          diff = target - now;
        }
      }
      
      // Never expire when daily reset is enabled - timer always resets
      if (diff <= 0 && !this.dailyReset) {
        // Hide immediately without animation on initial load (only if not daily reset)
        this.handleExpired(false);
        return;
      }
      
      const update = () => {
        // Recalculate target if daily reset is enabled (in case we crossed midnight)
        if (this.dailyReset) {
          target = updateTarget();
        }
        this.updateCountdown(target);
      };
      
      update();
      this.interval = window.setInterval(update, 1000);
    }

    updateCountdown(target) {
      const now = Date.now();
      let diff = target - now;
      
      // If daily reset is enabled and timer expired, reset to next midnight
      // This ensures the timer never expires - it always resets at midnight
      if (this.dailyReset && diff <= 0) {
        const nextMidnight = this.getNextMidnightUK();
        if (nextMidnight) {
          target = nextMidnight;
          diff = target - now;
        } else {
          // If we can't get next midnight, retry on next interval
          // Never call handleExpired when daily reset is enabled
          return;
        }
      }
      
      // Never expire when daily reset is enabled - timer always resets automatically
      if (diff <= 0 && !this.dailyReset) {
        // Timer expired while viewing - use animation (only if not daily reset)
        this.handleExpired(true);
        return;
      }
      
      const seconds = Math.floor(diff / 1000);
      const days = Math.floor(seconds / (24 * 3600));
      const hours = Math.floor((seconds % (24 * 3600)) / 3600);
      const minutes = Math.floor((seconds % 3600) / 60);
      const secs = seconds % 60;
      const values = [days, hours, minutes, secs];
      this.countdownBoxes.forEach((box, index) => {
        box.textContent = String(values[index]).padStart(2, '0');
      });
    }

    handleExpired(animated = true) {
      // Never handle expiration when daily reset is enabled - timer always resets automatically
      if (this.dailyReset) {
        return;
      }
      
      if (this.interval) {
        clearInterval(this.interval);
      }
      switch (this.expiredBehavior) {
        case 'hide_bar':
          this.hideBar(animated);
          break;
        case 'hide_countdown':
          if (this.countdownEl) this.countdownEl.hidden = true;
          break;
        case 'show_message':
          if (this.countdownEl) this.countdownEl.hidden = true;
          if (this.expiredMessageEl) {
            this.expiredMessageEl.hidden = false;
            this.expiredMessageEl.textContent = this.expiredText;
          }
          break;
        default:
          this.countdownBoxes.forEach((box) => {
            box.textContent = '00';
          });
      }
    }

    openPopup() {
      if (!this.popup) return;
      this.focusedBeforePopup = document.activeElement;
      this.popup.setAttribute('aria-hidden', 'false');
      this.popup.classList.remove(
        'announcement-static__popup--animate-scale',
        'announcement-static__popup--animate-fade',
        'announcement-static__popup--animate-slide'
      );
      const animationClass = this.getAnimationClass();
      if (animationClass) {
        this.popup.classList.add(animationClass);
      }
      this.lockScroll();
      this.trapFocus();
    }

    closePopup() {
      if (!this.popup) return;
      this.popup.setAttribute('aria-hidden', 'true');
      if (this.modal) {
        this.modal.style.transform = '';
      }
      this.unlockScroll();
      this.releaseFocus();
      if (this.focusedBeforePopup && typeof this.focusedBeforePopup.focus === 'function') {
        this.focusedBeforePopup.focus();
      }
    }

    lockScroll() {
      this.body.dataset.announcementScroll = this.body.style.overflow;
      this.body.style.overflow = 'hidden';
      document.addEventListener('focus', this.focusHandler, true);
      document.addEventListener('keydown', this.keyHandler);
    }

    unlockScroll() {
      if (this.body.dataset.announcementScroll !== undefined) {
        this.body.style.overflow = this.body.dataset.announcementScroll;
        delete this.body.dataset.announcementScroll;
      } else {
        this.body.style.overflow = '';
      }
      document.removeEventListener('focus', this.focusHandler, true);
      document.removeEventListener('keydown', this.keyHandler);
    }

    trapFocus() {
      if (!this.popup) return;
      this.focusableSelectors =
        'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])';
      this.focusableElements = Array.from(this.popup.querySelectorAll(this.focusableSelectors)).filter(
        (el) => el.offsetParent !== null || this.popup === el
      );
      const first = this.focusableElements[0];
      if (first) {
        first.focus();
      }
    }

    releaseFocus() {
      this.focusableElements = [];
    }

    handleFocusTrap(event) {
      if (!this.popup || this.popup.getAttribute('aria-hidden') === 'true') return;
      if (this.popup.contains(event.target)) {
        return;
      }
      event.stopPropagation();
      if (this.focusableElements && this.focusableElements.length) {
        this.focusableElements[0].focus();
      }
    }

    handleKeydown(event) {
      if (event.key === 'Escape') {
        this.closePopup();
      }
      if (event.key !== 'Tab') return;
      if (!this.focusableElements || !this.focusableElements.length) return;
      const first = this.focusableElements[0];
      const last = this.focusableElements[this.focusableElements.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    getAnimationClass() {
      switch (this.popupAnimation) {
        case 'fade':
          return 'announcement-static__popup--animate-fade';
        case 'slide-up':
          return 'announcement-static__popup--animate-slide';
        case 'scale':
          return 'announcement-static__popup--animate-scale';
        default:
          return '';
      }
    }

    copyCode(button) {
      const code = button.dataset.copyCode;
      const feedback = button.nextElementSibling;
      const successClass = 'is-success';
      const successState = () => {
        button.classList.add(successClass);
        if (feedback) {
          feedback.classList.add('is-visible');
        }
      };

      const resetState = () => {
        button.classList.remove(successClass);
        if (feedback) {
          feedback.classList.remove('is-visible');
        }
      };

      if (this.activeCopyTimeouts.has(button)) {
        clearTimeout(this.activeCopyTimeouts.get(button));
      }

      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard
          .writeText(code)
          .then(() => {
            successState();
            if (this.copyStatusEl) {
              this.copyStatusEl.textContent = window.theme?.strings?.copiedToClipboard || 'Copied to clipboard';
            }
            const timeout = setTimeout(() => {
              resetState();
              this.activeCopyTimeouts.delete(button);
            }, this.copyDuration);
            this.activeCopyTimeouts.set(button, timeout);
          })
          .catch(() => this.fallbackCopy(code, feedback));
      } else {
        this.fallbackCopy(code, feedback);
      }
    }

    fallbackCopy(code, feedback) {
      const tempInput = document.createElement('input');
      tempInput.value = code;
      document.body.appendChild(tempInput);
      tempInput.select();
      try {
        document.execCommand('copy');
        if (feedback) {
          feedback.textContent = 'Copied! (Ctrl+C if not)';
          feedback.classList.add('is-visible');
        }
        if (this.copyStatusEl) {
          this.copyStatusEl.textContent = 'Copied to clipboard';
        }
      } catch (error) {
        // console.warn('Announcement bar: clipboard unsupported', error);
      }
      document.body.removeChild(tempInput);
    }
  }

  function initSection(section) {
    if (!section) return;
    const root = section.querySelector(selectors.section);
    if (root) {
      initializeInstance(root);
    }
  }

  function initAll() {
    const sections = document.querySelectorAll(selectors.section);
    const blockingStyle = document.getElementById('announcement-bar-prevent-flash');
    if (true && typeof console !== 'undefined' && console.log) {
      console.log('[AnnouncementBar Debug][JRSBASE-V8.3.0-09102026-BYOB] initAll ran', {
        sectionCount: sections.length,
        hasBlockingStyle: !!blockingStyle,
        template: document.documentElement.getAttribute('data-page-template') || document.body?.className || 'unknown'
      });
    }
    sections.forEach((section) => {
      initializeInstance(section);
    });
  }

  function initializeInstance(root) {
    if (root.dataset.announcementStaticInit === 'true') return;
    root.dataset.announcementStaticInit = 'true';
    new AnnouncementBar(root);
  }

  ready(initAll);

  document.addEventListener('shopify:section:load', (event) => {
    initSection(event.target);
  });
})();

